import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function BulkUploadDialog({ open, onOpenChange, onSuccess }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/items/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "menumojo-bulk-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/items/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created} items`);
      onSuccess?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import from Excel</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="bg-muted/60 rounded-xl p-4 border border-border">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-5 h-5 brand-orange shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold">Need a starting template?</div>
                  <div className="text-foreground/60 text-xs mt-0.5">
                    Columns: <code>category</code>, <code>sub_category</code>, <code>name</code>, <code>description</code>, <code>price</code>, <code>dietary</code>, <code>photo_url</code>, <code>video_url</code>
                  </div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={downloadTemplate} data-testid="download-template-btn">
                    <Download className="w-4 h-4" /> Download template
                  </Button>
                </div>
              </div>
            </div>

            <label
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-brand-orange transition"
              data-testid="file-dropzone"
            >
              <Upload className="w-6 h-6 text-foreground/40" />
              <div className="text-sm font-semibold">{file ? file.name : "Click to choose .xlsx file"}</div>
              <div className="text-xs text-foreground/50">Categories and sub-categories are created automatically.</div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bulk-result">
            <div className="flex items-center gap-3 bg-veg/10 border border-veg/20 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-veg" />
              <div>
                <div className="font-semibold">Imported {result.created} items</div>
                <div className="text-xs text-foreground/60">Categories and sub-categories were auto-created as needed.</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4">
                <div className="flex items-center gap-2 text-destructive font-semibold text-sm">
                  <AlertTriangle className="w-4 h-4" /> {result.errors.length} row(s) skipped
                </div>
                <ul className="mt-2 text-xs text-foreground/70 space-y-1 max-h-32 overflow-auto">
                  {result.errors.map((e, i) => <li key={`${i}-${e.slice(0, 32)}`}>• {e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                className="bg-brand-orange hover:bg-[#E65C00] text-white"
                onClick={upload}
                disabled={!file || busy}
                data-testid="bulk-upload-submit"
              >
                {busy ? "Importing…" : "Import items"}
              </Button>
            </>
          ) : (
            <Button onClick={() => { reset(); onOpenChange(false); }} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid="bulk-done-btn">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
